import React, {useLayoutEffect, useRef} from "react";
import { useDispatch } from "react-redux";
import {calculateHeight, isSmWidth} from "../../../Apis/functions";
import TableFunction from "../../table/TableFunction";

/**
 * @Author: karen Yunuen Pozos Arias
 * @JobTitle : Full Stack Developer - IBL
 * @Email : kpozos@ibl.mx
 *
 * Hook-like component that manages the available items table logic,
 * including pagination, dynamic data fetching, and layout adjustments.
 *
 * @param {Object} params - Configuration parameters
 * @param {Object} params.results - Data source containing available items and pagination info
 * @param {Function} params.setResults - Updates the results state
 * @param {string} params.searchElement - Current search input value
 * @param {string} params.labelRowsPerPage - Label for rows per page in pagination
 * @param {Function} params.tableBuildBody - Function to build the request body for API requests
 * @param {Function} params.tableEndPoint - Endpoint function to fetch table data
 * @param {string} params.viewTypeName - View type identifier for rendering variations
 * @param {boolean} params.modified - Indicates if the data has been modified
 * @param {Function} params.setModified - Updates the modified state
 *
 * @returns {Object} Table handlers and state
 * @returns {Function} returns.getData - Function to fetch paginated data
 * @returns {Object} returns.pagination - Pagination configuration
 * @returns {number} returns.rowsPerPage - Number of rows per page
 * @returns {React.RefObject} returns.elementRef - Reference for dynamic height calculation of table container
 * @returns {React.RefObject} returns.notInformationRef - Reference for "no data" container height adjustment
 * @returns {Function} returns.t - Translation function
 */
const AvailableItemsTableApp = (
    {
        results,
        setResults,
        searchElement,
        labelRowsPerPage,
        tableBuildBody,
        tableEndPoint,
        viewTypeName,
        modified,
        setModified,
        bottom
    }
) => {
    const dispatch = useDispatch();
    const elementRef = useRef(null);
    const notInformationRef = useRef(null);
    const fixedTopValue = useRef(null);
    const fixedHeight = useRef(null);

    useLayoutEffect(() => {
        if (isSmWidth()) return;

        if (!fixedTopValue.current) {
            const anchor = elementRef.current ?? notInformationRef.current;
            if (!anchor) return;
            const rect = anchor.getBoundingClientRect();
            if (!rect.width && !rect.height) return;
            fixedTopValue.current = `${rect.top + bottom - 65}px`;
            fixedHeight.current = `${window.innerHeight - (rect.top + bottom)}px`;
        }

        if (elementRef.current) elementRef.current.style.setProperty('--top-value', fixedTopValue.current);
        if (notInformationRef.current) notInformationRef.current.style.height = fixedHeight.current;
    }, [results?.content]);

    /**
     * Executes a paginated search request using basic criteria.
     *
     * Builds the request body with the current page, page size, selected model,
     * and any additional criteria passed as arguments.
     * Triggers the search action through Redux and updates the results on success.
     */
    const getData = (page, { searchElement }) => {
        dispatch(tableEndPoint(tableBuildBody({page, rowsPerPage, searchElement }))).then(({ payload: { data: { data, success } } }) => {
                if (success === true) {
                    setResults(data);
                }
            },
        );
    };
    /**
     * Handle to build table
     */
    const { t, pagination, rowsPerPage } = TableFunction({
        data: results,
        labelRowsPerPage: labelRowsPerPage,
        hasContainer: true,
        fixedPagination: false,
        searchElement, //New
        getData,//New
        view: viewTypeName,
        modified,
        setModified
    });

    return {
        t,
        pagination,
        rowsPerPage,
        elementRef,
        notInformationRef,
        getData
    };

}

export default AvailableItemsTableApp;
